import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import {
  Box, Typography, Button, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Alert, Tooltip, Skeleton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import VerifiedIcon from '@mui/icons-material/VerifiedUser';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ApiErrorAlert from '../../components/ApiErrorAlert';
import { listDomains, registerDomain, verifyDomain, deleteDomain } from '../../api/developerApi';

const STATUS_COLORS = {
  VERIFIED: { bg: '#dcfce7', text: '#166534' },
  PENDING: { bg: '#fef9c3', text: '#854d0e' },
  FAILED: { bg: '#fee2e2', text: '#991b1b' },
};

export default function DomainsPage() {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [newDomain, setNewDomain] = useState(null);
  const [verifyResult, setVerifyResult] = useState({});
  const [deleteError, setDeleteError] = useState('');
  const { register, handleSubmit, reset, formState: { isSubmitting } } = useForm();

  const { data: domains, isLoading } = useQuery('domains', listDomains);

  const addMutation = useMutation(registerDomain, {
    onSuccess: (data) => {
      setNewDomain(data);
      qc.invalidateQueries('domains');
      reset();
    },
  });

  const verifyMutation = useMutation(verifyDomain, {
    onSuccess: (data, id) => {
      setVerifyResult((prev) => ({ ...prev, [id]: data.status }));
      qc.invalidateQueries('domains');
    },
  });

  const deleteMutation = useMutation(deleteDomain, {
    onSuccess: () => { setDeleteError(''); qc.invalidateQueries('domains'); },
    onError: (e) => setDeleteError(e.response?.data?.message || 'Cannot delete domain with active webhooks.'),
  });

  const onSubmit = ({ domain }) => addMutation.mutate({ domain });

  const handleCloseAdd = () => { setAddOpen(false); setNewDomain(null); reset(); addMutation.reset(); };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Domains</Typography>
          <Typography variant="body2" color="text.secondary">
            Verify domain ownership before registering webhooks
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}
          sx={{ bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, textTransform: 'none', fontWeight: 600 }}>
          Add domain
        </Button>
      </Box>

      {deleteError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError('')}>{deleteError}</Alert>}
      <ApiErrorAlert
        error={verifyMutation.error}
        onClose={() => verifyMutation.reset()}
        sx={{ mb: 2 }}
      />

      <Paper elevation={0} sx={{ border: '1px solid #E4E4E7', borderRadius: 2, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: 'background.default' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Domain</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Verified</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from({ length: 2 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 4 }).map((_, j) => <TableCell key={j}><Skeleton /></TableCell>)}
                </TableRow>
              ))
              : (domains || []).map((d) => {
                const colors = STATUS_COLORS[d.status] || STATUS_COLORS.PENDING;
                const result = verifyResult[d.id];
                return (
                  <TableRow key={d.id} hover>
                    <TableCell sx={{ fontSize: 13, fontFamily: 'monospace' }}>{d.domain}</TableCell>
                    <TableCell>
                      <Chip label={result || d.status} size="small"
                        sx={{ bgcolor: colors.bg, color: colors.text, fontWeight: 600, fontSize: 11 }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {d.verifiedAt ? new Date(d.verifiedAt).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {d.status !== 'VERIFIED' && (
                        <Tooltip title="Check DNS verification">
                          <IconButton size="small" color="primary"
                            aria-label="Check DNS verification"
                            onClick={() => verifyMutation.mutate(d.id)}>
                            <VerifiedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error"
                          aria-label="Delete domain"
                          onClick={() => deleteMutation.mutate(d.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            {!isLoading && (domains || []).length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary', fontSize: 13 }}>
                  No domains registered yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Add domain dialog */}
      <Dialog open={addOpen} onClose={handleCloseAdd} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Add domain</DialogTitle>
        <DialogContent>
          {newDomain ? (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Add the following DNS TXT record to verify ownership, then click Verify.
              </Alert>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', border: '1px solid #E4E4E7', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Record name</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    _vectros-verify.{newDomain.domain}
                  </Typography>
                  <IconButton size="small" aria-label="Copy record name" onClick={() => navigator.clipboard.writeText(`_vectros-verify.${newDomain.domain}`)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Record value</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {newDomain.verificationToken}
                  </Typography>
                  <IconButton size="small" aria-label="Copy record value" onClick={() => navigator.clipboard.writeText(newDomain.verificationToken)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
            </Box>
          ) : (
            <Box component="form" id="add-domain-form" onSubmit={handleSubmit(onSubmit)}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <ApiErrorAlert error={addMutation.error} />
              <TextField label="Domain" size="small" fullWidth placeholder="e.g. api.yourcompany.com"
                {...register('domain', { required: true })} />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {newDomain
            ? <Button onClick={handleCloseAdd} variant="contained"
                sx={{ bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, textTransform: 'none' }}>
                Done
              </Button>
            : <>
                <Button onClick={handleCloseAdd} sx={{ textTransform: 'none' }}>Cancel</Button>
                <Button type="submit" form="add-domain-form" variant="contained" disabled={isSubmitting}
                  sx={{ bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, textTransform: 'none', fontWeight: 600 }}>
                  {isSubmitting ? 'Adding…' : 'Add'}
                </Button>
              </>
          }
        </DialogActions>
      </Dialog>
    </Box>
  );
}
